import { useEffect, useState, useMemo } from 'react';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import type { JobPosition, JobPosting, Application } from '@/types';
import { mergeApplicationsForAnalytics } from '@/lib/applicationArchive';

export interface PortfolioAiCredits {
  /** Sum of `ai_company.granted` across all client companies. */
  totalGranted: number;
  /** Sum of `ai_company.remaining` across all client companies. */
  totalRemaining: number;
  /** `totalGranted - totalRemaining`. */
  totalUsed: number;
  /** True if any client company is on an unlimited plan. */
  anyUnlimited: boolean;
  /** Number of client billing statuses successfully loaded. */
  companiesLoaded: number;
}

export interface AgencyAggregatedData {
  positions: JobPosition[];
  postings: JobPosting[];
  applications: Application[];
  /** Map companyId → friendly name for cross-portfolio tables. */
  companyNameById: Record<string, string>;
  /** Sum of AI Credits across the whole portfolio (sprint B3). */
  portfolioAiCredits: PortfolioAiCredits;
  loading: boolean;
  error: string | null;
  companiesLoaded: number;
  companiesTotal: number;
}

/**
 * Aggregates data from ALL active client companies of an agency owner.
 *
 * Source-of-truth for the client list: `user.agency.clients` (populated
 * by BE `listAgencyClients` → filtered to permission=admin + isActive).
 * Replaces the previous `Object.keys(memberships)` fallback, which could
 * include non-admin / inactive companies and mis-matched the
 * `clientsUsed` counter in the header.
 *
 * Sprint dashboard_alignment_v1 (26/05/26 22:00):
 *   - A4: filter parity with BE listAgencyClients
 *   - B3: portfolio AI Credits sum
 *   - Performance: client fan-out moved to Promise.all (was sequential
 *     for..of). Tier cap (≤20 clients) makes parallel fan-out safe.
 */
export function useAgencyAggregatedData(): AgencyAggregatedData {
  const { user } = useAuth();
  const [positions, setPositions] = useState<JobPosition[]>([]);
  const [postings, setPostings] = useState<JobPosting[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [companyNameById, setCompanyNameById] = useState<Record<string, string>>({});
  const [portfolioAiCredits, setPortfolioAiCredits] = useState<PortfolioAiCredits>({
    totalGranted: 0,
    totalRemaining: 0,
    totalUsed: 0,
    anyUnlimited: false,
    companiesLoaded: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companiesLoaded, setCompaniesLoaded] = useState(0);
  const [companiesTotal, setCompaniesTotal] = useState(0);

  // Stable key for the active client cohort — re-loads when the agency
  // adds / removes / activates a client.
  const activeClients = useMemo(
    () => (user?.agency?.clients ?? []).filter(c => c.isActive),
    [user?.agency?.clients],
  );
  const cohortKey = useMemo(
    () => activeClients.map(c => c.companyId).sort().join(','),
    [activeClients],
  );

  useEffect(() => {
    if (user?.accountType !== 'agency_owner') return;
    if (activeClients.length === 0) {
      setPositions([]); setPostings([]); setApplications([]);
      setCompanyNameById({});
      setPortfolioAiCredits({
        totalGranted: 0, totalRemaining: 0, totalUsed: 0,
        anyUnlimited: false, companiesLoaded: 0,
      });
      setCompaniesTotal(0); setCompaniesLoaded(0);
      return;
    }

    setCompaniesTotal(activeClients.length);
    setLoading(true);
    setError(null);
    setCompaniesLoaded(0);

    const nameMap: Record<string, string> = {};
    for (const c of activeClients) nameMap[c.companyId] = c.name;
    setCompanyNameById(nameMap);

    let loaded = 0;
    const onCompanyDone = () => {
      loaded += 1;
      setCompaniesLoaded(loaded);
    };

    type CompanyBundle = {
      positions: JobPosition[];
      postings: JobPosting[];
      applications: Application[];
      ai: { granted: number; remaining: number; unlimited: boolean };
    };

    const loadOne = async (companyId: string): Promise<CompanyBundle> => {
      try {
        const pos = await api.positions.list(companyId);
        const taggedPositions = pos.map(p => ({ ...p, _companyId: companyId }));
        const [companyPostings, activeApps, archivedApps, billing] = await Promise.all([
          api.postings.listAll(companyId, pos),
          api.applications.listAllByCompany(companyId, pos),
          api.applications.listAllByCompany(companyId, pos, { archived: true }).catch(() => [] as Application[]),
          api.billing.getStatus(companyId).catch(() => null),
        ]);
        const aiBalance = (billing?.balances as Record<string, {
          granted: number; remaining: number; unlimited: boolean;
        }> | undefined)?.['ai_company'];
        onCompanyDone();
        return {
          positions: taggedPositions,
          postings: companyPostings,
          applications: mergeApplicationsForAnalytics(activeApps, archivedApps),
          ai: {
            granted: aiBalance?.granted ?? 0,
            remaining: aiBalance?.remaining ?? 0,
            unlimited: aiBalance?.unlimited ?? !!billing?.isUnlimited,
          },
        };
      } catch (err) {
        // Single-client failure → log + return empty bundle so the
        // aggregate still renders the rest of the portfolio.
        console.error(`Failed to load data for company ${companyId}:`, err);
        onCompanyDone();
        return {
          positions: [], postings: [], applications: [],
          ai: { granted: 0, remaining: 0, unlimited: false },
        };
      }
    };

    const run = async () => {
      // Parallel fan-out across clients (cap ≤20 from tier limits).
      const bundles = await Promise.all(activeClients.map(c => loadOne(c.companyId)));
      const allPositions: JobPosition[] = [];
      const allPostings: JobPosting[] = [];
      const allApplications: Application[] = [];
      let totalGranted = 0;
      let totalRemaining = 0;
      let anyUnlimited = false;
      let billingLoaded = 0;
      for (const b of bundles) {
        allPositions.push(...b.positions);
        allPostings.push(...b.postings);
        allApplications.push(...b.applications);
        totalGranted += b.ai.granted;
        totalRemaining += b.ai.remaining;
        if (b.ai.unlimited) anyUnlimited = true;
        if (b.ai.granted > 0 || b.ai.remaining > 0 || b.ai.unlimited) billingLoaded += 1;
      }
      setPositions(allPositions);
      setPostings(allPostings);
      setApplications(allApplications);
      setPortfolioAiCredits({
        totalGranted,
        totalRemaining,
        totalUsed: Math.max(0, totalGranted - totalRemaining),
        anyUnlimited,
        companiesLoaded: billingLoaded,
      });
      setLoading(false);
    };

    run().catch(err => {
      setError(err?.message || 'Failed to load aggregated data');
      setLoading(false);
    });
  }, [user?.accountType, cohortKey, activeClients]);

  return {
    positions,
    postings,
    applications,
    companyNameById,
    portfolioAiCredits,
    loading,
    error,
    companiesLoaded,
    companiesTotal,
  };
}
